import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ExportButton } from "@/components/export/ExportButton";
import { AddToCollectionDialog } from "@/components/collections/AddToCollectionDialog";
import { Table, ChevronDown, ChevronUp, MapPin, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ClassificationBadge } from "@/components/locations/ClassificationBadge";
import { OwnershipBadge } from "@/components/locations/OwnershipBadge";
import { sportTypeIcons } from "@/lib/sport-icons";

interface LocationRow {
  id: string;
  name: string;
  primary_type: string | null;
  ownership: string;
  lat: number;
  lng: number;
  hasSportField: boolean | null;
  sportTypes?: string[] | null;
  isMuted?: boolean;
}

interface LocationTableDrawerProps {
  locations: LocationRow[];
  onLocationClick: (locationId: string, lat: number, lng: number) => void;
  isSidebarOpen?: boolean;
}

export function LocationTableDrawer({
  locations,
  onLocationClick,
  isSidebarOpen,
}: LocationTableDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCollectionDialog, setShowCollectionDialog] = useState(false);

  const allSelected = locations.length > 0 && selectedIds.size === locations.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < locations.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(locations.map((l) => l.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      className={cn(
        "absolute bottom-0 left-0 z-10 transition-all duration-300 transition-[right]",
        isOpen ? "h-[40%]" : "h-0"
      )}
      style={{ right: isSidebarOpen ? '560px' : '0' }}
    >
      <div className="absolute -top-9 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          className="shadow-lg rounded-t-lg rounded-b-none border border-b-0 gap-1.5"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Table className="size-4" />
          {isOpen ? (
            <>
              Hide Table <ChevronDown className="size-3" />
            </>
          ) : (
            <>
              Show Table ({locations.length}){" "}
              <ChevronUp className="size-3" />
            </>
          )}
        </Button>
        <ExportButton />
      </div>

      {isOpen && (
        <div className="h-full bg-background border-t overflow-hidden flex flex-col">
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 px-3 py-2 bg-primary/10 border-b shrink-0">
              <span className="text-sm">
                <strong>{selectedIds.size}</strong> selected
              </span>
              <Button
                size="sm"
                variant="default"
                onClick={() => setShowCollectionDialog(true)}
              >
                Add to Collection
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedIds(new Set())}
              >
                <X className="size-3 mr-1" />
                Clear
              </Button>
            </div>
          )}
          <div className="overflow-auto flex-1">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                    Name
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                    Type
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                    Classification
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                    Sports
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                    Ownership
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-10" />
                </tr>
              </thead>
              <tbody>
                {locations.map((loc) => (
                  <tr
                    key={loc.id}
                    className={cn("border-b hover:bg-muted/30 cursor-pointer transition-colors", loc.isMuted && "opacity-50")}
                    onClick={() => onLocationClick(loc.id, loc.lat, loc.lng)}
                  >
                    <td className="px-3 py-2 w-10" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(loc.id)}
                        onCheckedChange={() => toggleSelect(loc.id)}
                        aria-label={`Select ${loc.name}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium truncate max-w-[200px]">
                      {loc.name}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {loc.primary_type?.replace(/_/g, " ") ?? "-"}
                    </td>
                    <td className="px-3 py-2">
                      <ClassificationBadge hasSportField={loc.hasSportField} />
                    </td>
                    <td className="px-3 py-2">
                      {loc.sportTypes && loc.sportTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {loc.sportTypes.map((type) => {
                            const Icon = sportTypeIcons[type];
                            const label = type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                            return (
                              <span
                                key={type}
                                className="inline-flex items-center gap-0.5 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                              >
                                {Icon && <Icon className="size-3" />}
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <OwnershipBadge
                        ownership={
                          loc.ownership as "public" | "private" | "unknown"
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <MapPin className="size-4 text-muted-foreground" />
                    </td>
                  </tr>
                ))}
                {locations.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-8 text-center text-muted-foreground"
                    >
                      No locations to display
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AddToCollectionDialog
        open={showCollectionDialog}
        onOpenChange={setShowCollectionDialog}
        locationIds={Array.from(selectedIds)}
        onComplete={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
